import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetch as fetchCompanyUsers } from "services/user-select.service";

interface UserOption {
  id: string;
  name: string;
  email: string;
}

interface Props {
  companyId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  /** Lets the card panel name a colleague the backend rejected by id. */
  onUsersLoaded?: (users: UserOption[]) => void;
  excludedUserIds?: string[];
  disabled?: boolean;
}

/**
 * Multi-select of company colleagues for Desk Share.
 * Loads non-guest company users (backend still enforces Access desk on share).
 */
export default function DeskShareUserPicker({
  companyId,
  value,
  onChange,
  onUsersLoaded,
  excludedUserIds = [],
  disabled,
}: Props) {
  const { t } = useTranslation();
  const listId = "desk-share-user-picker";
  const [users, setUsers] = useState<UserOption[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchCompanyUsers(companyId, { textSearch: "" }, false, undefined, true)
      .then((data: { usersCollection?: UserOption[] }) => {
        if (cancelled) return;
        const list = data.usersCollection || [];
        setUsers(list);
        if (onUsersLoaded) onUsersLoaded(list);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setUsers([]);
        setLoadError(e?.response?.data?.message || e?.message || t("desk_share_users_load_failed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, t, onUsersLoaded]);

  const excluded = useMemo(() => new Set(excludedUserIds), [excludedUserIds]);
  const selected = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => u && u.id && !excluded.has(u.id))
      .filter((u) => {
        if (!q) return true;
        const label = `${u.name || ""} ${u.email || ""}`.toLowerCase();
        return label.includes(q);
      });
  }, [users, excluded, query]);

  const toggle = (id: string) => {
    if (disabled) return;
    if (selected.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const selectedUsers = users.filter((u) => selected.has(u.id));

  return (
    <div className="space-y-2">
      <span id={`${listId}-label`} className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
        {t("desk_share_pick_users")}
      </span>
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(u.id)}
              title={t("desk_unshare")}
              aria-label={`${t("desk_unshare")}: ${u.name || u.email}`}
              className="inline-flex items-center gap-1 rounded-lg bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300 px-2 py-0.5 text-xs font-semibold max-md:min-h-11 max-md:px-3"
            >
              {u.name || u.email}
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled || loading}
        placeholder={t("desk_share_search_placeholder")}
        aria-labelledby={`${listId}-label`}
        className="w-full rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 max-md:min-h-11 max-md:text-base"
      />
      <div
        role="group"
        aria-labelledby={`${listId}-label`}
        className="min-h-[120px] max-h-48 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800 divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-950"
      >
        {loading && (
          <div className="px-3 py-3 text-xs text-zinc-500">{t("loading")}</div>
        )}
        {!loading && loadError && (
          <div className="px-3 py-3 text-xs text-red-600 dark:text-red-400">{loadError}</div>
        )}
        {!loading && !loadError && filtered.length === 0 && (
          <div className="px-3 py-3 text-xs text-zinc-500">{t("desk_share_users_empty")}</div>
        )}
        {!loading &&
          filtered.map((u) => {
            const checked = selected.has(u.id);
            return (
              <label
                key={u.id}
                className={
                  "flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900 max-md:min-h-11 " +
                  (checked ? "bg-violet-50/80 dark:bg-violet-950/20" : "")
                }
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(u.id)}
                  className="rounded border-zinc-300 shrink-0"
                />
                <span className="font-medium text-zinc-800 dark:text-zinc-200 truncate">
                  {u.name || u.email}
                </span>
                {u.name && u.email && (
                  <span className="text-xs text-zinc-500 truncate ml-auto">{u.email}</span>
                )}
              </label>
            );
          })}
      </div>
    </div>
  );
}
